import MaterialIcon from '@components/common/MaterialIcon';
import {
  ThirdPartySource,
  TrainingStatus,
} from '@conversed/rest/src/contracts/data-sources/data-sources.enums';
import { DATA_SOURCES_S3_BASE_PATH } from '@conversed/rest/src/contracts/data-sources/data-sources.schema';
import {
  ACCEPTED_FILE_TYPES,
  CONFIGS_BY_TRAINING_STATUS,
} from '@conversed/rest/src/contracts/data-sources/data-sources-config';
import { useDataSources } from '@hooks/dataSources/useDataSources';
import { useCompanySelector } from '@hooks/useCompanySelector';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoDataSourceDownloadButton } from './InfoDataSourceDownloadButton';

export const InfoDataSource: React.FC = () => {
  const { selectedDataSourceFile } = useDataSources();
  const { t } = useTranslation();
  const { siblingStudios } = useCompanySelector();

  const keyWithoutDataSourcePaths = useMemo(
    () =>
      selectedDataSourceFile?.key?.replace(
        `${DATA_SOURCES_S3_BASE_PATH}/${selectedDataSourceFile?.companyId}/`,
        '',
      ),
    [selectedDataSourceFile?.key, selectedDataSourceFile?.companyId],
  );

  const originalName = useMemo(() => {
    const isSharepoint =
      selectedDataSourceFile?.thirdPartySource ===
        ThirdPartySource.Sharepoint ||
      selectedDataSourceFile?.thirdPartySource ===
        ThirdPartySource.SharepointPages;

    if (isSharepoint) {
      return selectedDataSourceFile?.metadata?.originalName ?? 'Unknown';
    }

    return keyWithoutDataSourcePaths;
  }, [
    selectedDataSourceFile?.thirdPartySource,
    selectedDataSourceFile?.metadata?.originalName,
    keyWithoutDataSourcePaths,
  ]);

  const fileOwnerStudio = useMemo(
    () =>
      siblingStudios.find(
        (studio) => studio.id === selectedDataSourceFile?.companyId,
      ),
    [siblingStudios, selectedDataSourceFile?.companyId],
  );

  const configsByTrainingStatus = useMemo(
    () =>
      CONFIGS_BY_TRAINING_STATUS[
        selectedDataSourceFile?.trainingStatus || TrainingStatus.Ready
      ],
    [selectedDataSourceFile?.trainingStatus],
  );

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          <div className='col-span-1 flex gap-2 text-sm items-center'>
            <span className='font-medium'>
              {t('data-sources.training-status')}
            </span>
            <span className='italic flex items-center gap-0.5'>
              <MaterialIcon
                icon={configsByTrainingStatus?.icon}
                colorHex={configsByTrainingStatus.iconColor}
              />
              {t(configsByTrainingStatus.label)}
            </span>
          </div>

          {selectedDataSourceFile?.thirdPartySource && (
            <>
              <div className='tailwind-divider-x' />

              <div className='flex flex-col text-xs'>
                <div className='text-xs flex gap-2'>
                  <span className='font-medium'>
                    {t('data-sources.info.third-party-source')}
                  </span>
                  <span className='italic font-medium text-primary-base capitalize'>
                    {selectedDataSourceFile?.thirdPartySource}
                  </span>
                </div>
                <div className='text-xs flex gap-2'>
                  <span className='font-medium'>
                    {t('data-sources.info.third-party-source.sync-timestamp')}
                  </span>
                  <span className='italic'>
                    {selectedDataSourceFile?.lastThirdPartySync
                      ? new Date(
                          selectedDataSourceFile.lastThirdPartySync,
                        ).toLocaleString()
                      : ''}
                  </span>
                </div>
              </div>
            </>
          )}

          <div className='tailwind-divider-x' />

          <div className='col-span-1 flex flex-col'>
            <div className='text-base mb-2'>{t('data-sources.info.file')}</div>
            {selectedDataSourceFile?.label && (
              <div className='text-xs flex gap-2'>
                <span className='font-medium'>
                  {t('data-sources.info.label')}
                </span>
                <span className='italic font-medium text-primary-base'>
                  {selectedDataSourceFile.label}
                </span>
              </div>
            )}
            <div className='text-xs flex gap-2'>
              <span className='font-medium'>
                {t('data-sources.info.file.name')}
              </span>
              <span className='italic'>{originalName?.toString()}</span>
            </div>
            <div className='text-xs flex gap-2'>
              <span className='font-medium'>
                {t('data-sources.info.file.display-name')}
              </span>
              <span className='italic'>{selectedDataSourceFile?.name}</span>
            </div>
            <div className='text-xs flex gap-2'>
              <span className='font-medium'>
                {t('data-sources.info.document.type')}
              </span>
              <span className='italic'>
                {selectedDataSourceFile?.documentType || 'Unknown type'}
              </span>
            </div>
            <div className='text-xs flex gap-2'>
              <span className='font-medium'>
                {t('data-sources.info.file.type')}
              </span>
              <span className='italic'>
                {ACCEPTED_FILE_TYPES[selectedDataSourceFile?.fileType ?? ''] ||
                  'Unknown type'}
              </span>
            </div>
            <div className='text-xs flex gap-2'>
              <span className='font-medium'>
                {t('data-sources.info.file.size')}
              </span>
              <span className='italic'>
                {selectedDataSourceFile?.fileSize ?? 0} bytes
              </span>
            </div>
          </div>

          <div className='tailwind-divider-x' />

          <div className='flex flex-col gap-2 text-xs'>
            <div className='text-base mb-2'>
              {t('data-sources.info.duplicate-questions')}
            </div>
            <div className='flex flex-col text-xs gap-2 max-h-[350px] overflow-auto'>
              {selectedDataSourceFile ? (
                selectedDataSourceFile?.duplicateQuestions &&
                selectedDataSourceFile?.duplicateQuestions?.length > 0 ? (
                  selectedDataSourceFile.duplicateQuestions.map(
                    (duplicateQuestion, index) => (
                      <div key={index} className='flex flex-col'>
                        <span className='font-medium text-red-base'>
                          {duplicateQuestion.question}
                        </span>
                        <span className='italic'>
                          {t('data-sources.info.duplicate-questions-indexes')}{' '}
                          {duplicateQuestion.indices.join(', ')}
                        </span>
                      </div>
                    ),
                  )
                ) : (
                  <div className='flex flex-col'>
                    <span className='italic'>
                      {t('data-sources.info.no-duplicate-questions')}
                    </span>
                  </div>
                )
              ) : null}
            </div>
          </div>

          <div className='tailwind-divider-x' />

          <div className='flex flex-col gap-1 text-xs'>
            <div className='col-span-1 flex gap-2'>
              <span className='font-medium'>
                {t('data-sources.info.creation-date')}
              </span>
              <span className='italic'>
                {selectedDataSourceFile?.createdAt
                  ? new Date(selectedDataSourceFile.createdAt).toLocaleString()
                  : ''}
              </span>
            </div>
            <div className='col-span-1 flex gap-2'>
              <span className='font-medium'>
                {t('data-sources.info.updated-date')}
              </span>
              <span className='italic'>
                {selectedDataSourceFile?.updatedAt
                  ? new Date(selectedDataSourceFile.updatedAt).toLocaleString()
                  : ''}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className='tailwind-modal-footer'>
        <InfoDataSourceDownloadButton fileOwnerStudio={fileOwnerStudio} />
      </div>
    </div>
  );
};

export default InfoDataSource;
